/**
 * Hoofdmenu for the desktop bar and the mobile drawer.
 * Doors and rails come from the Hoofdmenu theme blocks. A rail with a Shopify
 * menu uses that menu for its columns and links. A rail without a menu keeps
 * the matching entry from assets/bx-mega-menu.json.
 * Special blocks (Cadeaus / Buitenkeukens) inject the matching JSON layouts.
 * The drawer keeps the theme markup (li.sub, a.toggle) so its drill-down still works.
 */

const CLOSE_DELAY_MS = 160

export function initMegaNav() {
  const root = document.querySelector('[data-bx-mega]')
  if (!root) return

  const blockDoors = readBlockDoors(root)
  const src = root.getAttribute('data-src')
  const needsJson =
    blockDoors.length === 0 ||
    blockDoors.some(
      (door) =>
        door.entries.some((entry) => entry.special || (entry.kind === 'rail' && !entry.menu))
    )

  const apply = (items) => {
    const doors = blockDoors.length ? mergeDoors(items, blockDoors) : items
    if (!doors.length) return
    mountMobileNav(doors)
    mount(root, doors)
  }

  if (!needsJson) {
    apply([])
    return
  }

  if (!src) {
    if (blockDoors.length) apply([])
    return
  }

  fetch(src)
    .then((response) => {
      if (!response.ok) throw new Error(`mega menu ${response.status}`)
      return response.json()
    })
    .then((data) => {
      const items = data && Array.isArray(data.items) ? data.items : []
      if (items.length || blockDoors.length) apply(items)
    })
    .catch((error) => {
      console.error(error)
      if (blockDoors.length) apply([])
    })
}

export function readBlockDoors(root) {
  return [...root.querySelectorAll('[data-bx-door]')].map((door) => {
    const entries = [...door.querySelectorAll('script[data-bx-rail], script[data-bx-special], script[data-bx-divider]')]
      .map((node) => {
        if (node.hasAttribute('data-bx-divider')) return { kind: 'divider' }
        try {
          const data = JSON.parse(node.textContent)
          const special = node.getAttribute('data-bx-special')
          if (special) {
            const menu = Array.isArray(data.menu) && data.menu.length ? data.menu : null
            const cards = Array.isArray(data.cards) ? data.cards : []
            return {
              kind: 'special',
              special,
              title: data.title || '',
              href: data.href || '',
              lead: data.lead || '',
              listTitle: data.listTitle || '',
              menuLabel: data.menuLabel || '',
              menu,
              cards,
            }
          }
          const menu = Array.isArray(data.menu) && data.menu.length ? data.menu : null
          return {
            kind: 'rail',
            title: data.title || '',
            href: data.href || '',
            sourceTitle: data.sourceTitle || data.title || '',
            layout: data.layout || 'columns',
            stack: stackTitles(data.stack),
            menu,
          }
        } catch {
          return null
        }
      })
      .filter(Boolean)

    return {
      title: door.dataset.title || '',
      href: door.dataset.href || '',
      sourceTitle: door.dataset.source || door.dataset.title || '',
      panel: door.dataset.panel || 'auto',
      entries,
      // Rails-only view kept for older call sites / debugging.
      rails: entries.filter((entry) => entry.kind === 'rail'),
    }
  })
}

export function mergeDoors(jsonItems, blockDoors) {
  return blockDoors.map((door) => {
    const buiten = door.entries.find((entry) => entry.special === 'buitenkeukens')
    if (buiten) return doorFromBuitenkeukensSpecial(jsonItems, door, buiten)

    const jsonDoor = findDoor(jsonItems, door)
    // A Shopify menu, a Special or a divider block means the blocks set the rail order.
    const linked = door.entries.some(
      (entry) => entry.special || entry.kind === 'divider' || (entry.kind === 'rail' && entry.menu)
    )
    if (!linked) {
      if (!jsonDoor) {
        return {
          title: door.title,
          href: door.href,
          groups: [{ items: door.entries.map((entry) => ({ title: entry.title, href: entry.href, children: [] })) }],
        }
      }
      return withBlockLabels(jsonDoor, door)
    }
    return doorFromEntries(jsonDoor, door, jsonItems)
  })
}

function findDoor(jsonItems, door) {
  return jsonItems.find((item) => item.title === door.sourceTitle)
    || jsonItems.find((item) => door.href && item.href === door.href)
    || null
}

function findRail(jsonDoor, sourceTitle) {
  if (!jsonDoor || !sourceTitle) return null
  for (const group of jsonDoor.groups || []) {
    for (const item of group.items || []) {
      if (item.title === sourceTitle) return item
    }
  }
  return null
}

function withBlockLabels(jsonDoor, door) {
  const copy = JSON.parse(JSON.stringify(jsonDoor))
  if (door.title) copy.title = door.title
  if (door.href) copy.href = door.href
  for (const rail of door.rails) {
    const item = findRail(copy, rail.sourceTitle)
    if (!item) continue
    if (rail.title) item.title = rail.title
    if (rail.href) item.href = rail.href
    markStacked(item.children, rail.stack)
  }
  return copy
}

function findSpecialCadeaus(jsonItems) {
  for (const door of jsonItems) {
    for (const group of door.groups || []) {
      for (const item of group.items || []) {
        if (item.layout === 'split' && /cadeau/i.test(item.title || '')) {
          return item
        }
      }
    }
  }
  return null
}

function buildCadeausSpecial(entry, jsonItems) {
  const preset = findSpecialCadeaus(jsonItems)
  const listTitle = entry.listTitle || 'Cadeau Inspiratie'
  const jsonCards = preset
    ? JSON.parse(JSON.stringify(preset.children || [])).filter((child) => child.image)
    : []
  const cards = mergeCadeausCards(entry.cards || [], jsonCards)
  const jsonList = preset
    ? (preset.children || []).find((child) => !child.image)
    : null
  const listChildren = entry.menu
    ? entry.menu.map((link) => ({ title: link.title || '', href: link.href || '' }))
    : JSON.parse(JSON.stringify((jsonList && jsonList.children) || []))
  return {
    title: entry.title || (preset && preset.title) || 'Cadeaus',
    href: entry.href || (preset && preset.href) || '',
    layout: 'split',
    children: [
      {
        title: listTitle,
        children: listChildren,
      },
      ...cards,
    ],
  }
}

function mergeCadeausCards(configured, jsonCards) {
  const count = Math.max(configured.length, jsonCards.length)
  const cards = []
  for (let i = 0; i < count; i++) {
    const conf = configured[i] || {}
    const base = jsonCards[i] ? JSON.parse(JSON.stringify(jsonCards[i])) : {}
    const title = (conf.title && String(conf.title).trim()) || base.title || ''
    const text = (conf.text && String(conf.text).trim()) || base.text || ''
    const cta = (conf.cta && String(conf.cta).trim()) || base.cta || ''
    const href = conf.href || base.href || ''
    const image = conf.image || base.image || ''
    if (!title && !image && !href) continue
    cards.push({ title, text, cta, href, image })
  }
  return cards
}

function findSpecialBuitenkeukens(jsonItems) {
  return (
    jsonItems.find((door) => door.layout === 'tiles' && /buitenkeuken/i.test(door.title || '')) ||
    jsonItems.find((door) => door.layout === 'tiles') ||
    null
  )
}

function doorFromBuitenkeukensSpecial(jsonItems, door, special) {
  const preset = findSpecialBuitenkeukens(jsonItems)
  const jsonCards = []
  const jsonFooter = { label: '', items: [] }
  for (const group of (preset && preset.groups) || []) {
    for (const item of group.items || []) {
      if (item.type === 'divider') continue
      if (item.image) jsonCards.push(JSON.parse(JSON.stringify(item)))
      else jsonFooter.items.push(JSON.parse(JSON.stringify(item)))
    }
    if (group.label && !jsonFooter.label) jsonFooter.label = group.label
  }

  const tiles = mergeBuitenkeukensTiles(special.cards || [], jsonCards)
  const menuLabel = (special.menuLabel && special.menuLabel.trim()) || jsonFooter.label || 'Ook ontdekken'
  const footerItems = special.menu
    ? special.menu.map((link) => ({ title: link.title || '', href: link.href || '' }))
    : jsonFooter.items

  const groups = [{ items: tiles }]
  if (footerItems.length) {
    groups.push({ label: menuLabel, items: footerItems })
  }

  const lead = (special.lead && special.lead.trim()) || (preset && preset.lead) || ''
  return {
    title: door.title || special.title || (preset && preset.title) || 'Buitenkeukens',
    href: door.href || special.href || (preset && preset.href) || '',
    layout: 'tiles',
    lead,
    groups,
  }
}

function mergeBuitenkeukensTiles(configured, jsonCards) {
  const slots = configured.length ? configured.slice(0, 4) : [{}, {}, {}, {}]
  while (slots.length < 4) slots.push({})
  const tiles = []
  for (let i = 0; i < 4; i++) {
    const conf = slots[i] || {}
    const base = jsonCards[i] ? JSON.parse(JSON.stringify(jsonCards[i])) : {}
    const title = (conf.title && String(conf.title).trim()) || base.title || ''
    const subtitle = (conf.text && String(conf.text).trim()) || base.eyebrow || base.text || ''
    const href = conf.href || base.href || ''
    const image = conf.image || base.image || ''
    if (!title && !image && !href) continue
    const featured = i === 0 || conf.featured === true || base.featured === true
    const tile = {
      title,
      href,
      image,
      featured,
      // Non-featured tiles stay edge-to-edge photo; studio keeps a pale frame.
      tone: featured ? 'studio' : 'photo',
      cta: base.cta || '',
    }
    if (featured) {
      if (subtitle) tile.eyebrow = subtitle
      if (Array.isArray(base.swatches) && base.swatches.length) tile.swatches = base.swatches
    } else if (subtitle) {
      tile.text = subtitle
    }
    tiles.push(tile)
  }
  return tiles
}

function doorFromEntries(jsonDoor, door, jsonItems) {
  const items = door.entries.map((entry) => {
    if (entry.kind === 'divider') return { type: 'divider' }
    if (entry.special === 'cadeaus') {
      return buildCadeausSpecial(entry, jsonItems)
    }
    if (entry.kind === 'rail' && entry.menu) {
      return {
        title: entry.title,
        href: entry.href,
        layout: entry.layout === 'split' ? 'split' : undefined,
        children: markStacked(entry.menu, entry.stack),
      }
    }
    const jsonItem = findRail(jsonDoor, entry.sourceTitle)
    if (!jsonItem) return { title: entry.title, href: entry.href, children: [] }
    const copy = JSON.parse(JSON.stringify(jsonItem))
    if (entry.title) copy.title = entry.title
    if (entry.href) copy.href = entry.href
    markStacked(copy.children, entry.stack)
    return copy
  })
  const layout = door.panel === 'tiles' ? 'tiles' : jsonDoor && jsonDoor.layout
  return {
    title: door.title,
    href: door.href || (jsonDoor && jsonDoor.href) || '',
    layout,
    lead: jsonDoor && jsonDoor.lead,
    groups: [{ items }],
  }
}

function isTouchLike(pointerType) {
  return pointerType === 'touch' || pointerType === 'pen'
}

function mount(root, doors) {
  root.removeAttribute('hidden')
  const assetMap = readAssetMap(root)

  let bar = root.querySelector('.bx-mega__bar')
  if (!bar) {
    bar = el('div', 'bx-mega__bar')
    root.replaceChildren(bar)
  }

  const markSrc = root.getAttribute('data-mark')
  if (markSrc) {
    let mark = bar.querySelector('.bx-mega__mark')
    if (!mark) {
      mark = markLink(markSrc)
      bar.prepend(mark)
    }
    mark.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return
      if (root.classList.contains('is-stuck')) close()
    })
  }
  const panel = el('div', 'bx-mega__panel')
  panel.id = 'bx-mega-panel'
  panel.hidden = true
  const sheet = el('div', 'bx-mega__sheet')
  const rail = el('div', 'bx-mega__rail')
  const main = el('div', 'bx-mega__main')
  sheet.append(rail, main)
  panel.append(sheet)
  root.append(bar, panel)

  const backdrop = el('div', 'bx-mega__backdrop')
  backdrop.setAttribute('aria-hidden', 'true')
  const page = document.getElementById('root') || document.body
  page.append(backdrop)

  let openIndex = -1
  let activeKey = ''
  let closeTimer = 0

  const existing = [...bar.querySelectorAll('.bx-mega__door')]
  const entries = doors.map((door, index) => {
    const panelDoor = hasPanel(door)
    const href = door.href || (firstItem(door) && firstItem(door).href)
    const wanted = href ? 'A' : 'BUTTON'
    let control = existing[index]
    if (!control || control.tagName !== wanted) {
      const created = href ? el('a', 'bx-mega__door') : el('button', 'bx-mega__door')
      if (control) control.replaceWith(created)
      else bar.append(created)
      control = created
    }
    control.textContent = door.title
    if (href) control.href = href
    if (panelDoor) {
      if (!href) control.type = 'button'
      control.setAttribute('aria-expanded', 'false')
      control.setAttribute('aria-controls', panel.id)
      control.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return
        open(index)
      })
      // Touch and pen have no hover: the first tap opens the panel, a second tap
      // on the open door follows its link. Mouse and keyboard keep following the link.
      let lastPointerType = ''
      control.addEventListener('pointerdown', (event) => {
        lastPointerType = event.pointerType
      })
      control.addEventListener('click', (event) => {
        const pointerType = lastPointerType
        lastPointerType = ''
        if (!href) {
          if (openIndex === index) close()
          else open(index)
          return
        }
        if (!isTouchLike(pointerType) || openIndex === index) return
        event.preventDefault()
        open(index)
      })
    } else {
      control.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') close()
      })
    }
    control.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault()
        const next = event.key === 'ArrowRight' ? index + 1 : index - 1
        focusEntry((next + entries.length) % entries.length)
      }
      if (event.key === 'ArrowDown' && panelDoor) {
        event.preventDefault()
        open(index)
        const link = sheet.querySelector('.bx-mega__tile, .bx-mega__rail-link')
        if (link) link.focus()
      }
    })
    return control
  })
  existing.slice(doors.length).forEach((node) => node.remove())

  root.addEventListener('pointerenter', () => window.clearTimeout(closeTimer))
  root.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'mouse') return
    closeTimer = window.setTimeout(close, CLOSE_DELAY_MS)
  })
  root.addEventListener('focusout', (event) => {
    if (!root.contains(event.relatedTarget)) close()
  })
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openIndex >= 0) {
      const control = entries[openIndex]
      close()
      control.focus()
    }
  })
  document.addEventListener('pointerdown', (event) => {
    if (!root.contains(event.target)) close()
  })

  document.documentElement.classList.add('bx-mega-ready')
  watchSticky(root)

  function focusEntry(index) {
    entries[index].focus()
    if (hasPanel(doors[index])) open(index)
    else close()
  }

  function open(index) {
    if (!hasPanel(doors[index])) {
      close()
      return
    }
    window.clearTimeout(closeTimer)
    const door = doors[index]
    const switching = openIndex !== index
    openIndex = index
    entries.forEach((control, controlIndex) => {
      const isOpen = controlIndex === index
      control.classList.toggle('is-open', isOpen)
      if (control.hasAttribute('aria-controls')) control.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
    })
    panel.hidden = false
    backdrop.classList.add('is-visible')
    if (switching) {
      activeKey = ''
      const tiles = door.layout === 'tiles'
      sheet.classList.toggle('is-tiles', tiles)
      panel.classList.toggle('is-tiles', tiles)
      if (tiles) renderTiles(door)
      else {
        renderRail(door)
        const first = firstItem(door)
        if (first) showItem(first)
      }
    }
  }

  let packObserver = null

  function close() {
    openIndex = -1
    activeKey = ''
    panel.hidden = true
    panel.classList.remove('is-tiles')
    sheet.classList.remove('is-tiles')
    backdrop.classList.remove('is-visible')
    if (packObserver) {
      packObserver.disconnect()
      packObserver = null
    }
    rail.replaceChildren()
    main.replaceChildren()
    entries.forEach((control) => {
      control.classList.remove('is-open')
      if (control.hasAttribute('aria-controls')) control.setAttribute('aria-expanded', 'false')
    })
  }

  function renderTiles(door) {
    if (packObserver) {
      packObserver.disconnect()
      packObserver = null
    }
    rail.replaceChildren()
    main.replaceChildren()

    const head = el('div', 'bx-mega__tiles-head')
    const title = door.href ? el('a', 'bx-mega__tiles-title') : el('p', 'bx-mega__tiles-title')
    if (door.href) title.href = door.href
    title.textContent = door.title
    head.append(title)
    if (door.lead) {
      const lead = el('p', 'bx-mega__tiles-lead')
      lead.textContent = door.lead
      head.append(lead)
    }
    main.append(head)

    const cards = []
    const footers = []
    for (const group of door.groups || []) {
      const plain = []
      for (const item of group.items || []) {
        if (item.type === 'divider') continue
        if (item.image) cards.push(item)
        else plain.push(item)
      }
      if (plain.length) footers.push({ label: group.label || '', items: plain })
    }

    const featured = cards.find((item) => item.featured)
    const ordered = featured ? [featured, ...cards.filter((item) => item !== featured)] : cards
    const grid = el('div', 'bx-mega__tiles')
    if (featured) {
      grid.classList.add('has-featured')
      grid.style.setProperty('--bx-tile-rest', String(Math.max(ordered.length - 1, 1)))
    }
    for (const item of ordered) grid.append(tileCard(item))
    main.append(grid)

    for (const footer of footers) {
      const row = el('div', 'bx-mega__tiles-more')
      if (footer.label) {
        const label = el('p', 'bx-mega__tiles-more-label')
        label.textContent = footer.label
        row.append(label)
      }
      const links = el('div', 'bx-mega__tiles-more-links')
      for (const item of footer.items) {
        const link = el('a', 'bx-mega__tiles-more-link')
        link.href = item.href || '#'
        link.textContent = item.title
        links.append(link)
      }
      row.append(links)
      main.append(row)
    }
  }

  function renderRail(door) {
    rail.replaceChildren()
    for (const group of door.groups || []) {
      if (group.label) {
        const label = el('p', 'bx-mega__label')
        label.textContent = group.label
        rail.append(label)
      }
      for (const item of group.items || []) {
        if (item.type === 'divider') {
          const rule = el('div', 'bx-mega__rail-divider')
          rule.setAttribute('role', 'separator')
          rail.append(rule)
          continue
        }
        const link = el('a', 'bx-mega__rail-link')
        link.href = item.href || '#'
        link.textContent = item.title
        link.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return
          showItem(item)
        })
        link.addEventListener('focus', () => showItem(item))
        // Touch and pen: the first tap on an item with children shows its columns,
        // a second tap on the shown item follows its link. Focus fires before click
        // and already calls showItem, so remember the state at pointerdown.
        let tapState = null
        link.addEventListener('pointerdown', (event) => {
          tapState = { pointerType: event.pointerType, wasActive: activeKey === itemKey(item) }
        })
        link.addEventListener('click', (event) => {
          const state = tapState
          tapState = null
          if (!state || !isTouchLike(state.pointerType) || state.wasActive) return
          if (!(item.children && item.children.length)) return
          event.preventDefault()
          showItem(item)
        })
        rail.append(link)
      }
    }
  }

  function itemKey(item) {
    return `${item.title}|${item.href || ''}`
  }

  function showItem(item) {
    const key = itemKey(item)
    if (key === activeKey) return
    activeKey = key
    for (const link of rail.querySelectorAll('.bx-mega__rail-link')) {
      const selected = link.textContent === item.title && link.getAttribute('href') === (item.href || '#')
      link.classList.toggle('is-active', selected)
      if (selected) link.setAttribute('aria-current', 'true')
      else link.removeAttribute('aria-current')
    }
    renderMain(item)
  }

  function mountColumns(container, groups) {
    const cols = el('div', 'bx-mega__cols')
    container.append(cols)
    let packedWidth = -1
    const pack = () => {
      const width = cols.clientWidth
      if (!width || width === packedWidth) return
      packedWidth = width
      packColumns(cols, groups)
    }
    pack()
    packObserver = new ResizeObserver(pack)
    packObserver.observe(cols)
  }

  function renderMain(item) {
    if (packObserver) {
      packObserver.disconnect()
      packObserver = null
    }
    main.replaceChildren()
    const head = el('div', 'bx-mega__main-head')
    const title = el('a', 'bx-mega__main-title')
    title.href = item.href || '#'
    title.textContent = item.title
    head.append(title)

    const children = item.children || []
    const isPlain = (child) => !child.lead && !child.text && !(child.children && child.children.length)
    const hasPlain = children.some(isPlain)
    const featured = children.filter((child) => child.lead || child.text || (hasPlain && child.children && child.children.length))
    const rest = children.filter((child) => !featured.includes(child))
    if (children.length && item.href && item.layout !== 'split') {
      const all = el('a', 'bx-mega__all')
      all.href = item.href
      all.textContent = 'Alles bekijken'
      head.append(all)
    }
    main.append(head)

    if (item.layout === 'split') {
      main.append(splitLayout(children))
      return
    }

    if (!children.length) return

    if (children.every((child) => child.children && child.children.length)) {
      mountColumns(main, columnUnits(children))
      return
    }

    if (rest.length) {
      const nested = rest.some((child) => child.children && child.children.length)
      if (!nested) {
        const list = el('div', 'bx-mega__links')
        for (const child of rest) list.append(childLink(child))
        main.append(list)
      } else {
        mountColumns(main, columnUnits(rest))
      }
    }
    for (const block of featured) main.append(featureBlock(block))
  }

  function resolveSrc(value) {
    if (!value || !value.startsWith('asset:')) return value || ''
    const name = value.slice('asset:'.length)
    if (assetMap[name]) return assetMap[name]
    const src = root.getAttribute('data-src') || ''
    return src.replace(/[^/?#]+(\?[^#]*)?$/, name)
  }

  function splitLayout(children) {
    const row = el('div', 'bx-mega__split')
    for (const child of children) {
      row.append(child.image ? giftCard(child) : linkColumn(child))
    }
    return row
  }

  function linkColumn(child) {
    const col = el('div', 'bx-mega__col')
    const heading = el('p', 'bx-mega__col-title')
    heading.textContent = child.title
    col.append(heading)
    for (const grand of child.children || []) col.append(childLink(grand))
    return col
  }

  function giftCard(child) {
    const link = el('a', 'bx-mega__gift')
    link.href = child.href || '#'
    const img = document.createElement('img')
    img.src = resolveSrc(child.image)
    img.alt = ''
    img.decoding = 'async'
    link.append(img)
    if (child.title) {
      const title = el('span', 'bx-mega__gift-title')
      title.textContent = child.title
      link.append(title)
    }
    if (child.text) {
      const text = el('p', 'bx-mega__gift-text')
      text.textContent = child.text
      link.append(text)
    }
    if (child.cta) {
      const cta = el('span', 'bx-mega__gift-cta')
      cta.textContent = child.cta
      link.append(cta)
    }
    return link
  }
}

const COLUMN_MIN = 190
const COLUMN_GAP = 28

function packColumns(container, groups) {
  const width = container.clientWidth
  const count = Math.min(groups.length, Math.max(1, Math.floor((width + COLUMN_GAP) / (COLUMN_MIN + COLUMN_GAP))))
  container.replaceChildren()
  const stacks = Array.from({ length: count }, () => {
    const stack = el('div', 'bx-mega__stack')
    container.append(stack)
    return stack
  })
  const heights = Array(count).fill(0)
  groups.forEach((group, groupIndex) => {
    let index = groupIndex < count ? groupIndex : 0
    if (groupIndex >= count) {
      for (let i = 1; i < count; i++) {
        if (heights[i] <= heights[index]) index = i
      }
    }
    stacks[index].append(...group)
    heights[index] = stacks[index].offsetHeight
  })
}

function readAssetMap(root) {
  try {
    const data = JSON.parse(root.getAttribute('data-assets') || '{}')
    return data && typeof data === 'object' ? data : {}
  } catch {
    return {}
  }
}

function hasPanel(door) {
  return (door.groups || []).some((group) => group.items && group.items.length)
}

function firstItem(door) {
  for (const group of door.groups || []) {
    for (const item of group.items || []) {
      if (item.type === 'divider') continue
      return item
    }
  }
  return null
}

// Rail setting "Onder de vorige kolom": one column title per line.
function stackTitles(value) {
  return String(value || '')
    .split('\n')
    .map(titleKey)
    .filter(Boolean)
}

function titleKey(title) {
  return String(title || '')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function markStacked(children, titles) {
  if (!titles || !titles.length) return children
  for (const child of children || []) {
    if (titles.includes(titleKey(child.title))) child.stack = true
  }
  return children
}

// One unit per column. A stacked child sits under the previous one.
function columnUnits(children) {
  const units = []
  for (const child of children) {
    if (child.stack && units.length) units[units.length - 1].push(columnGroup(child))
    else units.push([columnGroup(child)])
  }
  return units
}

function columnGroup(child) {
  const col = el('div', 'bx-mega__col')
  const heading = el('a', 'bx-mega__col-title')
  heading.href = child.href || '#'
  heading.textContent = child.title
  col.append(heading)
  if (child.lead) {
    const lead = el('p', 'bx-mega__feature-lead')
    lead.textContent = child.lead
    col.append(lead)
  }
  if (child.text) {
    const text = el('p', 'bx-mega__feature-text')
    text.textContent = child.text
    col.append(text)
  }
  for (const grand of child.children || []) col.append(childLink(grand))
  return col
}

function featureBlock(item) {
  const block = el('div', 'bx-mega__feature')
  const heading = el('a', 'bx-mega__feature-title')
  heading.href = item.href || '#'
  heading.textContent = item.title
  block.append(heading)
  if (item.lead) {
    const lead = el('p', 'bx-mega__feature-lead')
    lead.textContent = item.lead
    block.append(lead)
  }
  if (item.text) {
    const text = el('p', 'bx-mega__feature-text')
    text.textContent = item.text
    block.append(text)
  }
  const children = item.children || []
  if (children.length) {
    const list = el('div', 'bx-mega__links')
    for (const child of children) list.append(childLink(child))
    block.append(list)
  }
  return block
}

function tileCard(item) {
  const featured = item.featured === true
  const photo = item.tone === 'photo'
  const link = el('a', `bx-mega__tile${featured ? ' is-featured' : ''}${photo ? ' is-photo' : ' is-studio'}`)
  link.href = item.href || '#'

  if (featured) {
    const top = el('span', 'bx-mega__tile-copy')
    if (item.eyebrow) {
      const eye = el('span', 'bx-mega__tile-eyebrow')
      eye.textContent = item.eyebrow
      top.append(eye)
    }
    const title = el('span', 'bx-mega__tile-title')
    title.textContent = item.title
    top.append(title)
    link.append(top)
  }

  if (item.image) {
    const media = el('span', 'bx-mega__tile-media')
    const img = document.createElement('img')
    img.src = item.image
    img.alt = ''
    img.decoding = 'async'
    media.append(img)
    link.append(media)
  }

  const foot = el('span', featured ? 'bx-mega__tile-foot' : 'bx-mega__tile-copy')
  if (!featured) {
    const title = el('span', 'bx-mega__tile-title')
    title.textContent = item.title
    foot.append(title)
    if (item.text) {
      const text = el('span', 'bx-mega__tile-text')
      text.textContent = item.text
      foot.append(text)
    }
  }
  if (item.cta) {
    const cta = el('span', 'bx-mega__tile-cta')
    cta.textContent = item.cta
    foot.append(cta)
  }
  if (featured && Array.isArray(item.swatches) && item.swatches.length) {
    const swatches = el('span', 'bx-mega__swatches')
    swatches.setAttribute('aria-hidden', 'true')
    for (const color of item.swatches) {
      const dot = el('span', 'bx-mega__swatch')
      dot.style.background = color
      swatches.append(dot)
    }
    foot.append(swatches)
  }
  link.append(foot)
  return link
}

function childLink(item) {
  const link = el('a', 'bx-mega__child')
  link.href = item.href || '#'
  link.textContent = item.title
  return link
}

function markLink(src) {
  const mark = el('a', 'bx-mega__mark')
  mark.href = '/'
  mark.setAttribute('aria-label', 'Beef Exclusief')
  const img = document.createElement('img')
  img.src = src
  img.alt = ''
  img.width = 34
  img.height = 38
  mark.append(img)
  return mark
}

/*
 * Desktop: the bar stays at the top of the screen. The whole header section is position: sticky with a
 * negative top (everything above the bar, --bx-sticky-top), so the browser keeps it in place on the
 * compositor. Switching to position: fixed from an IntersectionObserver lagged on iOS (iPad landscape):
 * the bar first scrolled away and then jumped back. The observer now only toggles .is-stuck for the
 * looks (logo mark, colours), which changes nothing in the layout.
 */
function watchSticky(root) {
  const section = root.closest('.shopify-section') || root.parentElement
  const sentinel = el('div', 'bx-mega__sentinel')
  sentinel.setAttribute('aria-hidden', 'true')
  section.before(sentinel)

  const desktop = window.matchMedia('(min-width: 1001px)')

  function measure() {
    const offset = root.getBoundingClientRect().top - section.getBoundingClientRect().top
    section.style.setProperty('--bx-sticky-top', `${-offset}px`)
    // Marks where the bar starts sticking: the bar's own top in the page.
    sentinel.style.top = `${section.offsetTop + offset}px`
  }

  function setStuck(stuck) {
    root.classList.toggle('is-stuck', stuck && desktop.matches)
  }

  measure()
  if ('ResizeObserver' in window) {
    new ResizeObserver(() => {
      // Measuring while stuck would read the stuck position, not the natural one.
      if (!root.classList.contains('is-stuck')) measure()
    }).observe(section)
  }

  const observer = new IntersectionObserver(([entry]) => {
    setStuck(!entry.isIntersecting && entry.boundingClientRect.top < 0)
  })
  observer.observe(sentinel)
  desktop.addEventListener('change', () => {
    measure()
    setStuck(sentinel.getBoundingClientRect().top < 0)
  })
}

function mountMobileNav(doors) {
  const nav = document.getElementById('nav')
  if (!nav) return
  const list = nav.querySelector('ul[data-type="main-nav"]')
  if (!list) return

  const themeReady = !!nav.querySelector('ul.ul-inner')
  const viewAll = toggleLabel(list, ':scope > li > a.toggle')
  const nested = toggleLabel(list, 'li li a.toggle') || viewAll

  list.replaceChildren(...doors.map((door, index) => mobileDoor(door, index, viewAll, nested)))
  list.setAttribute('data-bx-mobile', 'blocks')
  // The theme copies #nav-bar into this list and hides every item after that
  // copy. Those clones are the old menu the hamburger was still showing.
  purgeInjectedMobileItems(nav)
  watchInjectedMobileItems(nav)
  mountMobileSheet(nav, doors)

  if (!themeReady) return

  list.querySelectorAll(':scope > li > a.toggle').forEach((toggle) => {
    const back = toggle.cloneNode(true)
    back.classList.add('toggle-back')
    toggle.parentElement.prepend(back)
  })
  const items = [...list.children]
  items.forEach((item, index) => item.setAttribute('data-index', String(items.length - index)))

  const inner = nav.querySelector('ul.ul-inner')
  if (inner) inner.replaceChildren(...items.map((item) => item.cloneNode(true)))

  items.forEach((item) => {
    item.addEventListener('mouseenter', () => showMobileHover(nav, item))
  })
  bindMobileToggles(nav)
}

function toggleLabel(list, selector) {
  const node = list.querySelector(selector)
  const text = node ? node.textContent.trim() : ''
  return text || 'Submenu'
}

function mobileDoor(door, index, viewAll, nested) {
  const entries = mobileEntries(door)
  const li = document.createElement('li')
  const href = door.href || ''
  if (entries.length) li.classList.add('sub')
  if (!href) li.classList.add('inactive')

  const link = document.createElement('a')
  link.href = href || './'
  link.textContent = door.title || ''
  li.append(link)

  const em = document.createElement('em')
  em.textContent = `(${index + 1})`
  li.append(em)

  if (!entries.length) return li

  const toggle = document.createElement('a')
  toggle.href = './'
  toggle.className = 'toggle'
  toggle.textContent = viewAll
  li.append(toggle)

  const submenu = document.createElement('ul')
  const column = document.createElement('li')
  const columnList = document.createElement('ul')
  for (const entry of entries) columnList.append(mobileNode(entry, nested))
  column.append(columnList)
  submenu.append(column)
  li.append(submenu)
  return li
}

function mobileEntries(door) {
  const entries = []
  for (const group of door.groups || []) {
    const items = (group.items || []).filter((item) => item && item.type !== 'divider' && item.title)
    if (!items.length) continue
    if (group.label) entries.push({ label: group.label })
    entries.push(...items)
  }
  return entries
}

function mobileNode(item, nested) {
  if (item.label) {
    const li = document.createElement('li')
    li.className = 'bx-mobile-label'
    const span = document.createElement('span')
    span.textContent = item.label
    li.append(span)
    return li
  }

  const children = (item.children || []).filter((child) => child && child.type !== 'divider' && child.title)
  const li = document.createElement('li')
  const link = document.createElement('a')
  link.href = item.href || './'
  link.textContent = item.title || ''
  li.append(link)
  if (!children.length) return li

  li.classList.add('sub')
  const toggle = document.createElement('a')
  toggle.href = item.href || './'
  toggle.className = 'toggle'
  toggle.textContent = nested
  li.append(toggle)
  const submenu = document.createElement('ul')
  for (const child of children) submenu.append(mobileNode(child, nested))
  li.append(submenu)
  return li
}

function bindMobileToggles(nav) {
  if (nav.dataset.bxMobileBound) return
  nav.dataset.bxMobileBound = 'true'
  const onToggle = (event) => {
    const toggle = event.target.closest('a.toggle')
    if (!toggle || !toggle.closest('#nav ul[data-bx-mobile]')) return
    event.preventDefault()
    toggleMobileItem(nav, toggle)
  }
  nav.addEventListener('click', onToggle)
  nav.addEventListener('keyup', (event) => {
    if (event.key === ' ') onToggle(event)
  })
}

function toggleMobileItem(nav, toggle) {
  const item = toggle.parentElement
  const list = toggle.closest('ul')
  if (!item || !list) return
  const mobileHeader = nav.getElementsByClassName('header-before')[0]

  if (item.classList.contains('toggle')) {
    item.classList.remove('toggle', 'mtoggle')
    list.classList.remove('ul-toggle')
    if (item.hasAttribute('data-title') && mobileHeader) mobileHeader.classList.remove('data-title-active')
  } else {
    ;[...list.children].forEach((child) => child.classList.remove('toggle'))
    item.classList.add('toggle', 'mtoggle')
    list.classList.add('ul-toggle')
    const typed = toggle.closest('[data-type]')
    if (typed) nav.setAttribute('data-type', typed.getAttribute('data-type'))
    if (item.hasAttribute('data-title') && mobileHeader) {
      mobileHeader.textContent = item.getAttribute('data-title')
      mobileHeader.classList.add('data-title-active')
    }
  }

  if (!nav.querySelector('.ul-toggle')) nav.removeAttribute('data-type')
  document.documentElement.classList.toggle(
    'nav-hover',
    !!nav.querySelector('ul[data-bx-mobile] > li.sub.toggle')
  )
}

function mountMobileSheet(nav, doors) {
  nav.classList.add('bx-mobile-nav')
  document.documentElement.classList.add('bx-mega-ready')
  placeMobileHeaderLogo(nav)
  const contact = nav.querySelector('.m6cn')
  const socials = nav.querySelector(':scope > .l4sc, .bx-mobile-view > .l4sc')
  if (contact) {
    renameMobileContactTitle(contact)
    nav.append(contact)
  }
  if (socials) nav.append(socials)
  nav.querySelector('[data-bx-mobile-sheet]')?.remove()

  const sheet = document.createElement('div')
  sheet.className = 'bx-mobile-sheet'
  sheet.setAttribute('data-bx-mobile-sheet', '')
  const track = document.createElement('div')
  track.className = 'bx-mobile-track'
  sheet.append(track)
  nav.append(sheet)

  const rootNodes = doors.map(nodeFromDoor)
  let depth = 0
  let moving = false

  const place = () => {
    track.style.transform = `translateX(-${depth * 100}%)`
  }

  const settle = (done) => {
    moving = true
    let settled = false
    const finish = (event) => {
      if (settled) return
      if (event && event.type === 'transitionend' && (event.target !== track || event.propertyName !== 'transform')) return
      settled = true
      track.removeEventListener('transitionend', finish)
      moving = false
      done()
    }
    track.addEventListener('transitionend', finish)
    window.setTimeout(() => finish(), 450)
  }

  const push = (node) => {
    if (moving) return
    const target = resolveMobileTarget(node)
    const kids = navigableChildren(target)
    if (!kids.length) {
      if (usableHref(target.href)) window.location.href = target.href
      return
    }
    const view = renderMobileView(target.children, target, pop, push)
    track.append(view)
    depth += 1
    place()
    settle(() => {
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        view.querySelector('.bx-mobile-back')?.focus({ preventScroll: true })
      }
    })
  }

  function pop() {
    if (moving || depth === 0) return
    const leaving = track.lastElementChild
    depth -= 1
    place()
    settle(() => {
      if (leaving && leaving !== track.firstElementChild) leaving.remove()
    })
  }

  const reset = () => {
    depth = 0
    moving = false
    track.style.transition = 'none'
    place()
    ;[...track.children].slice(1).forEach((view) => view.remove())
    window.requestAnimationFrame(() => {
      track.style.transition = ''
    })
  }

  const rootView = renderMobileView(rootNodes, null, pop, push)
  const ctas = mobileHeaderCtas()
  if (ctas) rootView.append(ctas)
  if (contact) rootView.append(contact)
  if (socials) rootView.append(socials)
  track.append(rootView)
  place()
  placeMobileChrome(nav)

  sheet.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || depth === 0) return
    event.preventDefault()
    event.stopPropagation()
    pop()
  })

  const drawer = new MutationObserver(() => {
    if (!document.documentElement.classList.contains('m2a')) reset()
  })
  drawer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
}

/** Copy the header CTA buttons (Winkel, Workshops) into the drawer, below the menu. */
function mobileHeaderCtas() {
  const links = document.querySelectorAll('#nav-user li.bx-header-ctas a.bx-header-cta')
  if (!links.length) return null
  const wrap = el('div', 'bx-mobile-ctas')
  for (const link of links) {
    const copy = link.cloneNode(true)
    copy.className = link.classList.contains('bx-header-cta--season')
      ? 'bx-mobile-cta bx-mobile-cta--season'
      : 'bx-mobile-cta'
    wrap.append(copy)
  }
  return wrap
}

function placeMobileHeaderLogo(nav) {
  const apply = () => {
    const header = [...nav.children].find(
      (el) => el.classList?.contains('header') && !el.classList.contains('header-before')
    )
    if (!header) return false
    if (header.querySelector('.bx-mobile-logo')) return true

    const logoImg =
      document.querySelector('#logo img.logo-img.mobile-only') ||
      document.querySelector('#logo img.logo-img') ||
      document.querySelector('#logo img')
    const markSrc = document.querySelector('[data-bx-mega]')?.getAttribute('data-mark')
    const src = logoImg?.currentSrc || logoImg?.getAttribute('src') || markSrc
    if (!src) return true

    const link = document.createElement('a')
    link.className = 'bx-mobile-logo'
    link.href = document.querySelector('#logo a')?.getAttribute('href') || '/'
    link.setAttribute('aria-label', 'Beef Exclusief')
    const img = document.createElement('img')
    img.src = src
    img.alt = logoImg?.getAttribute('alt') || 'Beef Exclusief'
    img.width = Number(logoImg?.getAttribute('width')) || 140
    img.height = Number(logoImg?.getAttribute('height')) || 36
    link.append(img)
    header.replaceChildren(link)
    return true
  }

  if (apply()) return
  const observer = new MutationObserver(() => {
    if (apply()) observer.disconnect()
  })
  observer.observe(nav, { childList: true })
  window.setTimeout(() => observer.disconnect(), 3000)
}

function nodeFromDoor(door) {
  return {
    title: door.title || '',
    href: door.href || '',
    children: mobileEntries(door).map(nodeFromItem),
  }
}

function nodeFromItem(item) {
  if (item.label) return { label: item.label, children: [] }
  const children = (item.children || [])
    .filter((child) => child && child.type !== 'divider' && child.title)
    .map(nodeFromItem)
  return { title: item.title || '', href: item.href || '', children }
}

function navigableChildren(node) {
  return (node?.children || []).filter((child) => child && !child.label)
}

/** Follow single-child chains so one submenu does not create an extra screen. */
function resolveMobileTarget(node) {
  let current = node
  while (true) {
    const kids = navigableChildren(current)
    if (kids.length !== 1) break
    current = kids[0]
  }
  return current
}

function renderMobileView(nodes, heading, pop, push) {
  const view = document.createElement('div')
  view.className = 'bx-mobile-view'

  if (heading) {
    const head = document.createElement('div')
    head.className = 'bx-mobile-head'
    const title = usableHref(heading.href) ? document.createElement('a') : document.createElement('p')
    title.className = 'bx-mobile-title'
    title.textContent = heading.title || ''
    if (title.tagName === 'A') title.href = heading.href
    const actions = document.createElement('div')
    actions.className = 'bx-mobile-actions'
    const back = document.createElement('button')
    back.type = 'button'
    back.className = 'bx-mobile-back'
    const icon = document.createElement('i')
    icon.className = 'icon-chevron-left'
    icon.setAttribute('aria-hidden', 'true')
    back.append(icon, document.createTextNode('Terug'))
    back.addEventListener('click', () => {
      back.blur()
      pop()
    })
    actions.append(back)
    if (usableHref(heading.href)) {
      const all = document.createElement('a')
      all.className = 'bx-mobile-all'
      all.href = heading.href
      all.textContent = 'Alles bekijken'
      actions.append(all)
    }
    head.append(title, actions)
    view.append(head)
  }

  const list = document.createElement('ul')
  list.className = 'bx-mobile-list'
  for (const node of nodes) {
    if (node.label) {
      const label = document.createElement('li')
      label.className = 'bx-mobile-label'
      label.textContent = node.label
      list.append(label)
      continue
    }
    const item = document.createElement('li')
    const link = document.createElement('a')
    link.textContent = node.title || ''
    const target = resolveMobileTarget(node)
    const kids = navigableChildren(target)
    if (kids.length) {
      item.classList.add('has-children')
      link.href = usableHref(target.href) ? target.href : '#submenu'
      link.addEventListener('click', (event) => {
        event.preventDefault()
        push(node)
      })
    } else {
      link.href = usableHref(target.href) ? target.href : usableHref(node.href) ? node.href : './'
    }
    item.append(link)
    list.append(item)
  }
  view.append(list)
  return view
}

function usableHref(href) {
  return !!href && href !== '#' && href !== './' && href !== '#submenu'
}

function purgeInjectedMobileItems(nav) {
  const list = nav.querySelector('ul[data-bx-mobile]')
  if (!list || list.dataset.bxPurging) return
  const injected = [...list.children].filter((item) => item.classList.contains('nav-bar-element'))
  if (!injected.length) return
  list.dataset.bxPurging = 'true'
  injected.forEach((item) => item.remove())
  const inner = nav.querySelector('ul.ul-inner')
  if (inner) inner.replaceChildren(...[...list.children].map((item) => item.cloneNode(true)))
  delete list.dataset.bxPurging
}

function renameMobileContactTitle(contact) {
  if (!contact || contact.dataset.bxTitleSet) return
  const title = contact.querySelector('h1, h2, h3, h4, h5, h6')
  if (!title) return
  title.textContent = 'Bezoek onze winkel'
  contact.dataset.bxTitleSet = 'true'
}

function placeMobileChrome(nav) {
  const rootView = nav.querySelector('.bx-mobile-track > .bx-mobile-view')
  if (!rootView || nav.dataset.bxPlacing) return
  const contact = nav.querySelector('.m6cn')
  const socials = nav.querySelector(':scope > .l4sc')
  if (!contact && !socials) return
  renameMobileContactTitle(contact)
  if (contact?.parentElement === rootView && socials?.parentElement === rootView) return
  nav.dataset.bxPlacing = 'true'
  if (contact && contact.parentElement !== rootView) rootView.append(contact)
  if (socials && socials.parentElement !== rootView) rootView.append(socials)
  delete nav.dataset.bxPlacing
}

function watchInjectedMobileItems(nav) {
  if (nav.dataset.bxMobileWatch) return
  nav.dataset.bxMobileWatch = 'true'
  const observer = new MutationObserver(() => {
    purgeInjectedMobileItems(nav)
    placeMobileChrome(nav)
  })
  observer.observe(nav, { childList: true, subtree: true })
}

function showMobileHover(nav, item) {
  const inner = nav.querySelector('ul.ul-inner')
  if (!inner) return
  nav.classList.add('ul-hover')
  ;[...inner.children].forEach((clone) => {
    clone.classList.toggle('hover', clone.dataset.index === item.dataset.index)
  })
}

function el(tag, className) {
  const node = document.createElement(tag)
  node.className = className
  return node
}
